import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router';
import { Alert, Box, Button, Card, CardContent, CircularProgress, Link, Stack, TextField, Typography } from '@mui/material';

import { AccountError, linkAccount, requestPasswordLink, setPassword } from '../api/account';

// Kept in step with tools/app/passwords.py.
const MIN_LENGTH = 10;

function Frame({ title, children }: { title: string; children: ReactNode }) {
    return (
        <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2, bgcolor: '#faf7f2' }}>
            <Card variant="outlined" sx={{ width: '100%', maxWidth: 420 }}>
                <CardContent sx={{ p: 3 }}>
                    <Typography variant="h5" component="h1" sx={{ mb: 2 }}>{title}</Typography>
                    {children}
                </CardContent>
            </Card>
        </Box>
    );
}

/** No link, or one that no longer works: ask for a new one. */
function AskForLink({ expired }: { expired: boolean }) {
    const [email, setEmail] = useState('');
    const [sent, setSent] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const submit = async (event: FormEvent) => {
        event.preventDefault();
        setBusy(true);
        setError(null);
        try {
            await requestPasswordLink(email.trim());
            setSent(true);
        } catch (failure) {
            setError((failure as Error).message);
        } finally {
            setBusy(false);
        }
    };
    if (sent) {
        return (
            <Alert severity="success">
                Si un compte existe pour {email.trim()}, un e-mail vient d’y partir avec un lien
                valable une heure. Pensez à regarder dans les indésirables.
            </Alert>
        );
    }
    return (
        <Stack component="form" spacing={2} onSubmit={submit}>
            {expired && <Alert severity="warning">Ce lien a expiré ou a déjà servi.</Alert>}
            <Typography color="text.secondary">
                Indiquez votre adresse : vous recevrez un lien pour choisir un nouveau mot de passe.
            </Typography>
            <TextField
                label="Adresse e-mail" type="email" id="email" name="email" autoComplete="username email" required fullWidth
                value={email} onChange={(event) => setEmail(event.target.value)}
            />
            {error && <Alert severity="error">{error}</Alert>}
            <Button type="submit" variant="contained" disabled={busy || !email.trim()}>
                Recevoir un lien
            </Button>
        </Stack>
    );
}

/** /compte/mot-de-passe?jeton=…: the page an invitation or a forgotten
 * password's mail leads to. Without a token, or with a spent one, it asks
 * for a new link instead. */
export function PasswordPage() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('jeton') ?? '';
    const [password, setPasswordValue] = useState('');
    const [confirmation, setConfirmation] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [expired, setExpired] = useState(false);
    const [done, setDone] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    // The link's address, asked for first: a password manager files the new
    // password under the form's username field, and offers to generate one
    // only in a form it understands.
    const [email, setEmail] = useState<string | null>(null);

    useEffect(() => {
        if (!token) return;
        let cancelled = false;
        linkAccount(token)
            .then((link) => { if (!cancelled) setEmail(link.email); })
            .catch((failure) => {
                if (cancelled) return;
                if (failure instanceof AccountError && failure.status === 410) setExpired(true);
                else setError((failure as Error).message);
            });
        return () => { cancelled = true; };
    }, [token]);

    const tooShort = password.length > 0 && password.length < MIN_LENGTH;
    const mismatch = confirmation.length > 0 && confirmation !== password;

    const submit = async (event: FormEvent) => {
        event.preventDefault();
        setBusy(true);
        setError(null);
        try {
            const result = await setPassword(token, password);
            setDone(result.email);
        } catch (failure) {
            if (failure instanceof AccountError && failure.status === 410) {
                setExpired(true);
            } else {
                setError((failure as Error).message);
            }
        } finally {
            setBusy(false);
        }
    };

    if (done) {
        return (
            <Frame title="Mot de passe enregistré">
                <Stack spacing={2}>
                    <Alert severity="success">
                        Vous pourrez désormais vous connecter avec <strong>{done}</strong> et ce mot de passe.
                    </Alert>
                    <Button component={RouterLink} to="/" variant="contained">Aller au site</Button>
                </Stack>
            </Frame>
        );
    }
    if (!token || expired) {
        return (
            <Frame title="Mot de passe oublié">
                <AskForLink expired={expired} />
            </Frame>
        );
    }
    if (email === null && !error) {
        return <Frame title="Choisir votre mot de passe"><CircularProgress size={24} /></Frame>;
    }
    return (
        <Frame title="Choisir votre mot de passe">
            <Stack component="form" method="post" spacing={2} onSubmit={submit}>
                <TextField
                    label="Adresse" id="username" name="username" type="email" autoComplete="username" fullWidth
                    value={email ?? ''} slotProps={{ htmlInput: { readOnly: true } }}
                />
                <TextField
                    label="Mot de passe" type="password" id="new-password" name="new-password"
                    autoComplete="new-password" required fullWidth autoFocus
                    value={password} onChange={(event) => setPasswordValue(event.target.value)}
                    error={tooShort}
                    helperText={`Au moins ${MIN_LENGTH} caractères. Une phrase de quelques mots fait très bien l’affaire.`}
                />
                <TextField
                    label="Le même, une seconde fois" type="password" id="confirm-password" name="confirm-password"
                    autoComplete="new-password" required fullWidth
                    value={confirmation} onChange={(event) => setConfirmation(event.target.value)}
                    error={mismatch}
                    helperText={mismatch ? 'Les deux ne sont pas identiques.' : ' '}
                />
                {error && <Alert severity="error">{error}</Alert>}
                <Button
                    type="submit" variant="contained"
                    disabled={busy || password.length < MIN_LENGTH || confirmation !== password}
                >
                    Enregistrer
                </Button>
                <Link component={RouterLink} to="/compte/mot-de-passe" variant="body2" onClick={() => setExpired(false)}>
                    Recevoir un nouveau lien
                </Link>
            </Stack>
        </Frame>
    );
}
